import {gql} from '@apollo/client';
import {StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {RelevantItemsQuery, RelevantItemsQueryVariables} from 'relevant-items/__generated/RelevantItemsQuery';
import PresentationalRelevantItemsSection from 'relevant-items/PresentationalRelevantItemsSection';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export const RELEVANT_PROCESS_FRAGMENT = gql`
  fragment RelevantProcessGraphQL on ProcessNode {
    id
    modifiedAt
    createdAt
    editUrl
    folder {
      id
      name
    }
    processTaskSchema {
      ... on ProcessTaskSchemaNode {
        id
        clientOrigin
      }
    }
  }
`;

export const RELEVANT_ITEMS_FRAGMENT = gql`
  fragment RelevantItemsGraphQL on RelevantItemsNode {
    tagged {
      ... on FolderItemInterface {
        id
        name
        folder {
          id
          name
          source {
            id
            name
          }
        }
        modifiedAt
      }
      ... on StageEntryNode {
        id
        name
        modifiedAt
      }
    }
    processTasks {
      id
      name
      process {
        ...RelevantProcessGraphQL
      }
    }
    processOutputs {
      id
      name
      process {
        ...RelevantProcessGraphQL
      }
    }
    sequencesUsingAsPrimer {
      id
      bindStrand
      bindPosition
      sequence {
        id
        name
        startIndex
        modifiedAt
        folder {
          id
          source {
            id
            name
          }
        }
      }
    }
    filesMentioning {
      ... on FolderItemInterface {
        id
        name
        modifiedAt
        folder {
          id
          source {
            id
            name
          }
        }
      }
      ... on StageEntryNode {
        id
        name
        modifiedAt
      }
    }
    bulkAssembliesCreatingAsAmplifiedFragment {
      id
      name
      modifiedAt
      folder {
        id
        source {
          id
          name
        }
      }
    }
    bulkAssembliesCreatingAsConstruct {
      id
      name
      modifiedAt
      folder {
        id
        source {
          id
          name
        }
      }
    }
    bulkAssembliesUsingAsFragment {
      id
      name
      modifiedAt
      folder {
        id
        source {
          id
          name
        }
      }
    }
    workflows {
      id
      workflow {
        id
        humanId
        name
        modifiedAt
        source {
          id
          name
        }
      }
      stageEntriesMentioning {
        id
        stageEntry {
          id
          editUrl
          name
        }
        inputSamples {
          id
          name
        }
        registeredSamples {
          id
          name
        }
      }
    }
  }
  ${RELEVANT_PROCESS_FRAGMENT}
`;

export const RELEVANT_ITEMS_QUERY = gql`
  query RelevantItemsQuery($fileId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    folderItem(id: $fileId) {
      ... on BaseFolderItemInterface {
        id
        relevantItems {
          ...RelevantItemsGraphQL
        }
        relevantConvertedFromItems {
          ...RelevantItemsGraphQL
        }
      }
      ... on FolderItemInterface {
        id
        relevantItems {
          ...RelevantItemsGraphQL
        }
        relevantConvertedFromItems {
          ...RelevantItemsGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${RELEVANT_ITEMS_FRAGMENT}
`;

interface QueryingRelevantItemsSectionProps {
  fileId: string;
}

export default function QueryingRelevantItemsSection(props: QueryingRelevantItemsSectionProps): JSX.Element {
  return (
    <BenchlingQuery<RelevantItemsQuery, RelevantItemsQueryVariables>
      query={RELEVANT_ITEMS_QUERY}
      variables={{fileId: props.fileId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => {
        if (isInaccessible(data.folderItem)) {
          // If this is a read-only share link, the full folder item will be inaccessible and we don't want
          // to render a relevant items section at all
          return null;
        }
        return (
          <PresentationalRelevantItemsSection
            relevantItemsGraphQL={data.folderItem.relevantItems}
            relevantConvertedFromItemsGraphQL={data.folderItem.relevantConvertedFromItems}
            itemId={props.fileId}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export const relevantItemsSectionStyles = StyleSheet.create({
  disabledRefresh: {
    pointerEvents: 'none',
  },
});
